import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis
} from "recharts";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CalendarDays,
  CircleDot,
  Gauge,
  KeyRound,
  Languages,
  LayoutDashboard,
  Lock,
  LogOut,
  Mail,
  Menu,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Swords,
  Target,
  Trash2,
  Trophy,
  Upload,
  UserRound,
  Users,
  X
} from "lucide-react";
import { FormEvent, ReactNode, useMemo, useState } from "react";
import type { User } from "@supabase/supabase-js";
import { useEffect } from "react";
import { isSupabaseConfigured, supabase } from "./supabase";
import { useSupabaseData } from "./hooks/useSupabaseData";
import type { AppUser, DataIssue, Game, Goals, Metric, Opponent, Player, PlayerStats, Serve, View } from "./types";
import {
  aggregateStats,
  formatSpeed,
  makePlayerStats,
  outcomeState,
  passProbabilityBySpeed,
  previousDateRange,
  quantiles,
  serveTypeKey,
  serveTypeLabel,
  speedForMetric,
  summarizePlayer
} from "./utils/analytics";
import {
  displayScale,
  gameStats,
  onScale,
  practiceVsGame,
  receiverStats,
  seasonOf,
  serverStats,
  summarize
} from "./utils/games";
import serveBoostLogo from "./assets/serveboost-logo.png";
import personIcon from "./assets/person_icon.jpg";
import { ReportsView } from "./ReportsView";
import { dateLocaleTag, t, useLocale } from "./i18n";

const navItems = (): Array<{ id: View; label: string; icon: ReactNode }> => [
  { id: "dashboard", label: t("Dashboard"), icon: <LayoutDashboard size={18} /> },
  { id: "games", label: t("Games"), icon: <Swords size={18} /> },
  { id: "players", label: t("Players"), icon: <Users size={18} /> },
  { id: "reports", label: t("Reports"), icon: <BarChart3 size={18} /> },
  { id: "settings", label: t("Settings"), icon: <Settings size={18} /> }
];

const colors = ["#b21818", "#ff8a80", "#e5b54a", "#9ab8ff", "#d2a8ff", "#77d9df"];
const routes: Record<View, string> = {
  dashboard: "/dashboard",
  games: "/games",
  players: "/players",
  reports: "/reports",
  settings: "/settings"
};

function viewFromPath(pathname: string): View {
  const match = Object.entries(routes).find(([, path]) => path === pathname);
  return (match?.[0] as View | undefined) ?? "dashboard";
}

type PlayerDraft = {
  firstName: string;
  lastName: string;
  number: string;
  position: string;
  dateOfBirth: string;
  height: string;
  weight: string;
  yearsOfEligibility: string;
  image: string;
};

const emptyPlayerDraft = (): PlayerDraft => ({
  firstName: "",
  lastName: "",
  number: "",
  position: "Outside Hitter",
  dateOfBirth: "",
  height: "",
  weight: "",
  yearsOfEligibility: "",
  image: ""
});

function formatMeasure(value: string | number | null | undefined, empty = "—") {
  if (value === null || value === undefined || value === "") return empty;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return String(Math.round(numeric));
}

const playerToDraft = (player: Player): PlayerDraft => ({
  firstName: player.firstName,
  lastName: player.lastName,
  number: String(player.number || ""),
  position: player.position || t("Outside Hitter"),
  dateOfBirth: player.dateOfBirth ?? "",
  height: formatMeasure(player.height, ""),
  weight: formatMeasure(player.weight, ""),
  yearsOfEligibility: String(player.yearsOfEligibility ?? ""),
  image: player.image ?? ""
});

function useAuthUser() {
  const [user, setUser] = useState<User | null | undefined>(undefined);

  useEffect(() => {
    const clearHandoffFragment = () => {
      const hash = new URLSearchParams(window.location.hash.slice(1));
      if (!hash.has("access_token") && !hash.has("refresh_token")) return;
      window.history.replaceState({}, "", `${window.location.pathname}${window.location.search}`);
    };
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (active) {
        setUser(data.session?.user ?? null);
        if (data.session) clearHandoffFragment();
      }
    });
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session) clearHandoffFragment();
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  return user;
}

export function App() {
  // Subscribe so module-level t() calls re-render immediately when language changes.
  useLocale();
  const supabaseUser = useAuthUser();
  const [demoEnabled, setDemoEnabled] = useState(() => import.meta.env.VITE_DEMO_MODE === "true" || new URLSearchParams(window.location.search).get("demo") === "1" || localStorage.getItem("serveboost-demo") === "1");
  const [view, setView] = useState<View>(() => viewFromPath(window.location.pathname));
  const [metric, setMetric] = useState<Metric>("km/h");
  const [sidebarOpen, setSidebarOpen] = useState(() => window.matchMedia("(min-width: 1181px)").matches);

  useEffect(() => {
    const onPopState = () => setView(viewFromPath(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  if (supabaseUser === undefined && !demoEnabled) {
    return <div className="boot-screen"><Activity className="spin" /> {t("Loading ServeBoost")}</div>;
  }

  const user: AppUser | null = demoEnabled
    ? { uid: "demo", email: "demo@serveboost.test", isDemo: true }
    : supabaseUser ? { uid: supabaseUser.id, email: supabaseUser.email ?? null } : null;

  if (!user) {
    return <Login onDemo={() => {
      localStorage.setItem("serveboost-demo", "1");
      setDemoEnabled(true);
      window.history.replaceState({}, "", "/dashboard?demo=1");
      setView("dashboard");
    }} />;
  }

  const navigate = (next: View) => {
    setView(next);
    const suffix = user.isDemo ? "?demo=1" : "";
    window.history.pushState({}, "", `${routes[next]}${suffix}`);
  };

  return (
    <Shell
      currentView={view}
      metric={metric}
      sidebarOpen={sidebarOpen}
      onToggleSidebar={() => setSidebarOpen((open) => !open)}
      onCloseSidebar={() => setSidebarOpen(false)}
      onMetricChange={setMetric}
      onViewChange={(next) => {
        navigate(next);
        if (window.matchMedia("(max-width: 1180px)").matches) {
          setSidebarOpen(false);
        }
      }}
      onExitDemo={() => {
        localStorage.removeItem("serveboost-demo");
        setDemoEnabled(false);
        window.history.replaceState({}, "", "/");
      }}
      user={user}
    />
  );
}

function Shell({
  currentView,
  metric,
  sidebarOpen,
  onToggleSidebar,
  onCloseSidebar,
  onMetricChange,
  onViewChange,
  onExitDemo,
  user
}: {
  currentView: View;
  metric: Metric;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onCloseSidebar: () => void;
  onMetricChange: (metric: Metric) => void;
  onViewChange: (view: View) => void;
  onExitDemo: () => void;
  user: AppUser;
}) {
  const { players, serves, goals, opponents, games, goalDocId, loading, issue } = useSupabaseData(user.uid);
  const [dateRange, setDateRange] = useState<{ start?: string; end?: string }>({});
  const [selectedPlayerId, setSelectedPlayerId] = useState("all");
  const stats = useMemo(() => makePlayerStats(players, serves, dateRange), [players, serves, dateRange]);
  const team = useMemo(() => aggregateStats(stats), [stats]);
  const previousRange = useMemo(() => previousDateRange(dateRange, serves), [dateRange, serves]);
  const previousStats = useMemo(() => makePlayerStats(players, serves, previousRange), [players, serves, previousRange]);
  const previousTeam = useMemo(() => aggregateStats(previousStats), [previousStats]);
  const selectedStats = selectedPlayerId === "all" ? team : stats.find((stat) => stat.id === selectedPlayerId) ?? team;

  return (
    <div className={`app-shell ${sidebarOpen ? "sidebar-open" : "sidebar-collapsed"}`}>
      <Sidebar currentView={currentView} open={sidebarOpen} onClose={onCloseSidebar} onViewChange={onViewChange} />
      <main className="workspace">
        <header className="topbar">
          <div className="topbar-leading">
            <button
              className="icon-button sidebar-toggle"
              onClick={onToggleSidebar}
              aria-label={sidebarOpen ? t("Close navigation") : t("Open navigation")}
              aria-expanded={sidebarOpen}
            >
              <Menu size={20} />
            </button>
            <div>
              <p className="eyebrow">ServeBoost</p>
              <h1>{navItems().find((item) => item.id === currentView)?.label}</h1>
            </div>
          </div>
          <div className="topbar-actions">
            {user.isDemo && <span className="demo-pill">{t("Demo data")}</span>}
            <button className="avatar-button" onClick={() => user.isDemo ? onExitDemo() : void supabase.auth.signOut()} aria-label={user.isDemo ? t("Exit demo") : t("Sign out")}>
              <span>{user.email?.slice(0, 1).toUpperCase() ?? "S"}</span>
              <LogOut size={16} />
            </button>
          </div>
        </header>

        {issue && <DataIssueBanner issue={issue} />}
        {loading ? <LoadingPanel /> : (
          <>
            {currentView === "dashboard" && (
              <DashboardView
                dateRange={dateRange}
                goals={goals}
                metric={metric}
                onDateRangeChange={setDateRange}
                onSelectedPlayerChange={setSelectedPlayerId}
                players={players}
                previousTeam={previousTeam}
                selectedPlayerId={selectedPlayerId}
                selectedStats={selectedStats}
                stats={stats}
                team={team}
              />
            )}
            {currentView === "games" && <GamesView games={games} metric={metric} opponents={opponents} players={players} serves={serves} />}
            {currentView === "players" && <PlayersView isDemo={Boolean(user.isDemo)} players={players} uid={user.uid} />}
            {currentView === "reports" && (
              <ReportsView
                dateRange={dateRange}
                games={games}
                goals={goals}
                metric={metric}
                onDateRangeChange={setDateRange}
                opponents={opponents}
                players={players}
                serves={serves}
                user={user}
              />
            )}
            {currentView === "settings" && <SettingsView goals={goals} goalDocId={goalDocId} metric={metric} onMetricChange={onMetricChange} user={user} />}
          </>
        )}
      </main>
    </div>
  );
}

function Sidebar({
  currentView,
  open,
  onClose,
  onViewChange
}: {
  currentView: View;
  open: boolean;
  onClose: () => void;
  onViewChange: (view: View) => void;
}) {
  return (
    <>
      <aside className={`sidebar ${open ? "open" : ""}`} aria-hidden={!open}>
        <div className="brand-row">
          <img src={serveBoostLogo} alt={t("ServeBoost")} />
        </div>
        <nav>
          {navItems().map((item) => (
            <button key={item.id} className={currentView === item.id ? "active" : ""} onClick={() => onViewChange(item.id)}>
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>
      {open && <button className="nav-scrim" onClick={onClose} aria-label={t("Close navigation overlay")} />}
    </>
  );
}

function DashboardView({
  dateRange,
  goals,
  metric,
  onDateRangeChange,
  onSelectedPlayerChange,
  players,
  previousTeam,
  selectedPlayerId,
  selectedStats,
  stats,
  team
}: {
  dateRange: { start?: string; end?: string };
  goals: Goals;
  metric: Metric;
  onDateRangeChange: (range: { start?: string; end?: string }) => void;
  onSelectedPlayerChange: (id: string) => void;
  players: Player[];
  previousTeam: PlayerStats;
  selectedPlayerId: string;
  selectedStats: PlayerStats;
  stats: PlayerStats[];
  team: PlayerStats;
}) {
  const [activeServeType, setActiveServeType] = useState("all");
  const [activeOutcome, setActiveOutcome] = useState("all");
  const scopedServes = selectedStats.serves.filter((serve) => {
    if (activeServeType !== "all" && serve.type !== activeServeType) return false;
    if (activeOutcome !== "all" && serve.state !== outcomeState(activeOutcome)) return false;
    return true;
  });
  const scopedStats = useMemo(() => summarizePlayer(selectedStats, scopedServes), [selectedStats, scopedServes]);
  const topPlayers = stats.slice(0, 3);
  const serveTypeData = ["float", "hyb", "spin"].map((type) => ({
    type: t(serveTypeLabel(type)),
    efficiency: scopedStats.byType[type]?.efficiency ?? 0,
    speed: speedForMetric(scopedStats.byType[type]?.avgSpeed ?? 0, metric),
    goal: type === "float" ? goals.effiencyFloat : type === "hyb" ? goals.effiencyHybrid : goals.effiencySpin,
    count: scopedStats.byType[type]?.total ?? 0,
    aceRate: scopedStats.byType[type]?.total ? (scopedStats.byType[type].aces / scopedStats.byType[type].total) * 100 : 0,
    missRate: scopedStats.byType[type]?.total ? (scopedStats.byType[type].misses / scopedStats.byType[type].total) * 100 : 0
  }));
  const outcomeData = [
    { name: t("Pass"), value: scopedStats.passes },
    { name: t("Miss"), value: scopedStats.misses },
    { name: t("Ace"), value: scopedStats.aces }
  ].filter((entry) => entry.value > 0);
  const probabilityData = passProbabilityBySpeed(scopedStats, metric);
  const quantileData = stats.map((stat) => ({ name: stat.name, quantiles: quantiles(stat.serves.map((serve) => speedForMetric(serve.speed, metric))) })).filter((entry) => entry.quantiles);
  const strongestServer = stats[0];
  const teamRisk = team.totalServes ? (team.misses / team.totalServes) * 100 : 0;
  const teamAceRate = team.totalServes ? (team.aces / team.totalServes) * 100 : 0;
  const previousTeamRisk = previousTeam.totalServes ? (previousTeam.misses / previousTeam.totalServes) * 100 : 0;
  const previousTeamAceRate = previousTeam.totalServes ? (previousTeam.aces / previousTeam.totalServes) * 100 : 0;
  const inPlayRate = team.totalServes ? ((team.totalServes - team.misses) / team.totalServes) * 100 : 0;
  const goalRows = [
    { label: t("Float efficiency"), actual: scopedStats.byType.float?.efficiency ?? 0, goal: goals.effiencyFloat, suffix: "%", tone: "mint" },
    { label: t("Hybrid efficiency"), actual: scopedStats.byType.hyb?.efficiency ?? 0, goal: goals.effiencyHybrid, suffix: "%", tone: "blue" },
    { label: t("Spin efficiency"), actual: scopedStats.byType.spin?.efficiency ?? 0, goal: goals.effiencySpin, suffix: "%", tone: "amber" },
    { label: t("Float avg speed"), actual: speedForMetric(scopedStats.byType.float?.avgSpeed ?? 0, metric), goal: speedForMetric(goals.avgSpeedFloat, metric), suffix: ` ${metric}`, tone: "coral" }
  ];
  const trendRows = [
    { label: t("Efficiency"), value: team.efficiency, previous: previousTeam.efficiency, suffix: "%", precision: 1 },
    { label: t("Miss rate"), value: teamRisk, previous: previousTeamRisk, suffix: "%", precision: 1, inverse: true },
    { label: t("Ace rate"), value: teamAceRate, previous: previousTeamAceRate, suffix: "%", precision: 1 }
  ];
  const datedServes = scopedStats.serves.filter((serve) => serve.date).sort((a, b) => a.date!.getTime() - b.date!.getTime());
  const latestServeDate = datedServes[datedServes.length - 1]?.date ?? new Date();
  const periodLabel = dateRange.start || dateRange.end
    ? t("{start} – {end}", { start: dateRange.start || t("First serve"), end: dateRange.end || t("Latest") })
    : t("All recorded sessions");
  const playerLabel = selectedStats.id === "all" ? t("All players") : selectedStats.name;
  const weeklyData = Array.from({ length: 8 }, (_, index) => {
    const weekEnd = new Date(latestServeDate);
    weekEnd.setHours(23, 59, 59, 999);
    weekEnd.setDate(weekEnd.getDate() - ((7 - index) * 7));
    const weekStart = new Date(weekEnd);
    weekStart.setDate(weekStart.getDate() - 6);
    weekStart.setHours(0, 0, 0, 0);
    const weekServes = scopedStats.serves.filter((serve) => serve.date && serve.date >= weekStart && serve.date <= weekEnd);
    const aces = weekServes.filter((serve) => serve.state === 3).length;
    const misses = weekServes.filter((serve) => serve.state === 1).length;
    return {
      week: weekStart.toLocaleDateString(dateLocaleTag(), { month: "short", day: "numeric" }),
      efficiency: weekServes.length ? ((aces - misses) / weekServes.length) * 100 : 0,
      volume: weekServes.length
    };
  }).filter((week) => week.volume > 0);
  const quantileValues = quantileData.flatMap((entry) => [entry.quantiles!.min, entry.quantiles!.max]);
  const quantileMin = quantileValues.length ? Math.floor(Math.min(...quantileValues) / 10) * 10 : 0;
  const quantileMax = quantileValues.length ? Math.ceil(Math.max(...quantileValues) / 10) * 10 : 100;
  const quantileSpan = Math.max(1, quantileMax - quantileMin);
  const playerMapData = stats.filter((stat) => stat.totalServes > 0).map((stat) => ({
    name: stat.name,
    shortName: stat.firstName,
    speed: speedForMetric(stat.avgSpeed, metric),
    aceRate: (stat.aces / stat.totalServes) * 100,
    missRate: (stat.misses / stat.totalServes) * 100,
    volume: stat.totalServes
  }));
  const playerMapSpeedAverage = playerMapData.length ? playerMapData.reduce((sum, player) => sum + player.speed, 0) / playerMapData.length : 0;
  const playerMapAceAverage = playerMapData.length ? playerMapData.reduce((sum, player) => sum + player.aceRate, 0) / playerMapData.length : 0;

  function applyDatePreset(days?: number) {
    if (!days) {
      onDateRangeChange({});
      return;
    }
    const end = new Date(latestServeDate);
    const start = new Date(end);
    start.setDate(start.getDate() - (days - 1));
    onDateRangeChange({ start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) });
  }

  return (
    <div className="dashboard-grid">
      {players.length === 0 && <EmptyState title={t("No players yet")} body={t("Add your first player from the Players view.")} />}

      <section className="dashboard-intro">
        <p>{periodLabel} · {playerLabel}</p>
        <div className="quick-periods" aria-label={t("Date presets")}>
          <button className={!dateRange.start && !dateRange.end ? "active" : ""} onClick={() => applyDatePreset()}>{t("All time")}</button>
          <button onClick={() => applyDatePreset(30)}>{t("30 days")}</button>
          <button onClick={() => applyDatePreset(14)}>{t("14 days")}</button>
          <button onClick={() => applyDatePreset(7)}>{t("7 days")}</button>
        </div>
      </section>

      <section className="hero-panel">
        <div>
          <p className="eyebrow">{t("Team pulse")}</p>
          <h2>{t("{value}% efficiency", { value: team.efficiency.toFixed(1) })}</h2>
          <p><strong>{t("{count} tracked serves across {players} players. {rate}% landed in play.", { count: team.totalServes.toLocaleString(), players: players.length.toLocaleString(), rate: inPlayRate.toFixed(1) })}</strong></p>
          <OutcomeMix aces={team.aces} inPlay={team.passes} misses={team.misses} />
        </div>
        <div className="hero-metrics">
          <Kpi icon={<CircleDot />} label={t("Serve volume")} value={team.totalServes.toLocaleString()} meta={t("{count} playable", { count: team.passes.toLocaleString() })} />
          <Kpi icon={<Gauge />} label={t("Average speed")} value={formatSpeed(team.avgSpeed, metric)} meta={t("{rate}% miss rate", { rate: teamRisk.toFixed(1) })} />
          <Kpi icon={<Trophy />} label={t("Max speed")} value={formatSpeed(team.maxSpeed, metric)} meta={strongestServer ? strongestServer.name : t("No player data")} />
        </div>
      </section>

      <section className="trend-strip">
        {trendRows.map((trend, index) => <TrendCard key={trend.label} {...trend} featured={index === 0} />)}
      </section>

      <section className="controls-band">
        <div className="control-label"><SlidersHorizontal size={16} /> {t("Refine")}</div>
        <div className="field">
          <Search size={16} />
          <select value={selectedPlayerId} onChange={(event) => onSelectedPlayerChange(event.target.value)}>
            <option value="all">{t("All players")}</option>
            {stats.map((player) => <option key={player.id} value={player.id}>{player.name}</option>)}
          </select>
        </div>
        <div className="field">
          <CalendarDays size={16} />
          <input type="date" value={dateRange.start ?? ""} onChange={(event) => onDateRangeChange({ ...dateRange, start: event.target.value || undefined })} />
        </div>
        <div className="field">
          <CalendarDays size={16} />
          <input type="date" value={dateRange.end ?? ""} onChange={(event) => onDateRangeChange({ ...dateRange, end: event.target.value || undefined })} />
        </div>
        {(dateRange.start || dateRange.end) && <button className="text-button" onClick={() => onDateRangeChange({})}><X size={14} /> {t("Clear dates")}</button>}
        <div className="filter-pills">
          {["all", "float", "hyb", "spin"].map((type) => (
            <button key={type} className={activeServeType === type ? "active" : ""} onClick={() => setActiveServeType(type)}>
              {type === "all" ? t("All serves") : t(serveTypeLabel(type))}
            </button>
          ))}
          {["all", "Pass", "Ace", "Miss"].map((outcome) => (
            <button key={outcome} className={activeOutcome === outcome ? "active" : ""} onClick={() => setActiveOutcome(outcome)}>
              {outcome === "all" ? t("All outcomes") : t(outcome)}
            </button>
          ))}
          {(activeServeType !== "all" || activeOutcome !== "all") && (
            <button className="reset-filter" onClick={() => {
              setActiveServeType("all");
              setActiveOutcome("all");
            }}><RefreshCw size={13} /> {t("Reset")}</button>
          )}
        </div>
      </section>

      <section className="goal-panel">
        <div className="section-heading">
          <p className="eyebrow">{t("Goal progress")}</p>
          <h2>{t("Targets vs current output")}</h2>
        </div>
        <div className="goal-list">
          {goalRows.map((goal) => <GoalRow key={goal.label} {...goal} />)}
        </div>
      </section>

      <section className="section-band">
        <div className="section-heading row-heading">
          <div>
            <p className="eyebrow">{t("Leaderboard")}</p>
            <h2>{t("Best servers")}</h2>
          </div>
          <span className="section-chip">{t("{count} shown", { count: topPlayers.length })}</span>
        </div>
        <div className="top-player-grid">
          {topPlayers.map((player) => <PlayerCard key={player.id} player={player} metric={metric} />)}
        </div>
      </section>

      <section className="chart-grid">
        <ChartPanel title={t("{name}'s outcomes", { name: playerLabel })}>
          {outcomeData.length ? (
            <div className="donut-wrap">
              <div className="donut-chart">
                <div className="donut-center"><strong>{scopedStats.totalServes}</strong><span>{t("serves")}</span></div>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={outcomeData} innerRadius={68} outerRadius={96} paddingAngle={4} cornerRadius={5} dataKey="value" nameKey="name">
                      {outcomeData.map((_, index) => <Cell key={index} fill={colors[index % colors.length]} />)}
                    </Pie>
                    <Tooltip content={<AnalyticsTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="outcome-legend">
                {outcomeData.map((entry, index) => <div key={entry.name}><i style={{ background: colors[index % colors.length] }} /><span>{entry.name}</span><strong>{((entry.value / scopedStats.totalServes) * 100).toFixed(0)}%</strong></div>)}
              </div>
            </div>
          ) : <NoData />}
        </ChartPanel>

        <ChartPanel title={t("Risk and reward by serve")} meta={t("Ace rate vs miss rate")}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={serveTypeData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="type" />
              <YAxis domain={[0, "auto"]} tickFormatter={(value) => `${Number(value).toFixed(0)}%`} />
              <Tooltip cursor={{ fill: "rgba(255,255,255,.035)" }} content={<AnalyticsTooltip />} />
              <Legend iconType="circle" iconSize={8} />
              <Bar name={t("Ace rate")} dataKey="aceRate" fill="#6ce9b6" radius={[5, 5, 0, 0]} onClick={(entry) => setActiveServeType(serveTypeKey(String(entry.type)))} />
              <Bar name={t("Miss rate")} dataKey="missRate" fill="#ff7568" radius={[5, 5, 0, 0]} onClick={(entry) => setActiveServeType(serveTypeKey(String(entry.type)))} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel title={t("What happens as speed rises?")} meta={t("Volume + in-play rate · {metric}", { metric })}>
          {probabilityData.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={probabilityData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="range" />
                <YAxis yAxisId="rate" width={78} domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={(value) => `${Number(value).toFixed(0)}%`} label={verticalAxisTitle(t("In-play rate"))} />
                <YAxis yAxisId="volume" orientation="right" allowDecimals={false} hide />
                <Tooltip cursor={{ fill: "rgba(255,255,255,.035)" }} content={<AnalyticsTooltip />} />
                <Legend iconType="circle" iconSize={8} />
                <Bar name={t("Serve volume")} yAxisId="volume" dataKey="serves" fill="rgba(119,170,255,.18)" radius={[5, 5, 0, 0]} />
                <Line name={t("In-play rate")} yAxisId="rate" type="monotone" dataKey="probability" stroke="#77aaff" strokeWidth={3} dot={{ r: 4, fill: "#0f1412", strokeWidth: 2 }} />
              </ComposedChart>
            </ResponsiveContainer>
          ) : <NoData />}
        </ChartPanel>

        <ChartPanel title={t("8-week workload and efficiency")} meta={t("Context matters")}>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={weeklyData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="week" />
              <YAxis yAxisId="efficiency" width={78} domain={[-100, 100]} tickFormatter={(value) => `${value}%`} label={verticalAxisTitle(t("Efficiency"))} />
              <YAxis yAxisId="volume" orientation="right" allowDecimals={false} hide />
              <Tooltip content={<AnalyticsTooltip />} />
              <Legend iconType="circle" iconSize={8} />
              <Bar name={t("Serve volume")} yAxisId="volume" dataKey="volume" fill="rgba(108,233,182,.16)" radius={[5, 5, 0, 0]} />
              <Line name={t("Efficiency")} yAxisId="efficiency" type="monotone" dataKey="efficiency" stroke="#6ce9b6" strokeWidth={3} dot={{ r: 4, fill: "#0f1412", strokeWidth: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel title={t("Player performance map")} meta={t("Pace × ace rate · bubble = volume")} wide>
          {playerMapData.length ? (
            <ResponsiveContainer width="100%" height={340}>
              <ScatterChart margin={{ top: 18, right: 20, bottom: 12, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" dataKey="speed" name={t("Average speed")} domain={["dataMin - 3", "dataMax + 3"]} tickFormatter={(value) => Number(value).toFixed(0)} />
                <YAxis type="number" dataKey="aceRate" name={t("Ace rate")} unit="%" width={78} domain={[0, "dataMax + 5"]} label={verticalAxisTitle(t("Ace rate"))} />
                <ZAxis type="number" dataKey="volume" range={[90, 520]} name={t("Serve volume")} />
                <ReferenceLine x={playerMapSpeedAverage} stroke="rgba(148,164,160,.35)" strokeDasharray="4 4" />
                <ReferenceLine y={playerMapAceAverage} stroke="rgba(148,164,160,.35)" strokeDasharray="4 4" />
                <Tooltip cursor={{ strokeDasharray: "4 4" }} content={<PlayerMapTooltip metric={metric} />} />
                <Scatter name={t("Players")} data={playerMapData}>
                  {playerMapData.map((player) => <Cell key={player.name} fill={player.missRate > teamRisk ? "#ff7568" : "#6ce9b6"} fillOpacity={0.82} />)}
                  <LabelList dataKey="shortName" position="top" fill="#d3dfdb" fontSize={11} offset={10} />
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          ) : <NoData />}
          <div className="plot-note"><i className="safe" /> {t("Below-average miss risk")} <i className="risk" /> {t("Above-average miss risk")} <span>{t("Dashed lines show player averages")}</span></div>
        </ChartPanel>
      </section>

      <section className="section-band">
        <div className="section-heading row-heading">
          <div>
            <p className="eyebrow">{t("Distribution")}</p>
            <h2>{t("Speed quantiles")}</h2>
          </div>
          <span className="section-chip">{t("Median focus")}</span>
        </div>
        <div className="quantile-list">
          {quantileData.length ? quantileData.map((entry) => {
            const q = entry.quantiles!;
            const q1Left = Math.max(0, Math.min(100, ((q.q1 - quantileMin) / quantileSpan) * 100));
            const iqrWidth = Math.max(2, Math.min(100, ((q.q3 - q.q1) / quantileSpan) * 100));
            const medianLeft = Math.max(0, Math.min(100, ((q.median - quantileMin) / quantileSpan) * 100));
            return (
              <div className="quantile-row" key={entry.name}>
                <span>{entry.name}</span>
                <div className="quantile-track">
                  <i style={{ left: `${q1Left}%`, width: `${iqrWidth}%` }} />
                  <b style={{ left: `${medianLeft}%` }} />
                  <div className="quantile-tooltip" role="tooltip">
                    <span><em>{t("Min")}</em>{q.min.toFixed(1)}</span>
                    <span><em>Q1</em>{q.q1.toFixed(1)}</span>
                    <span><em>{t("Median")}</em>{q.median.toFixed(1)}</span>
                    <span><em>Q3</em>{q.q3.toFixed(1)}</span>
                    <span><em>{t("Max")}</em>{q.max.toFixed(1)}</span>
                    <span className="quantile-tooltip-unit">{metric}</span>
                  </div>
                </div>
                <strong>{q.median.toFixed(1)} {metric}</strong>
              </div>
            );
          }) : <NoData />}
        </div>
      </section>
    </div>
  );
}

// One bar split into ace / in play / miss, with the shares underneath
function OutcomeMix({ aces, inPlay, misses }: { aces: number; inPlay: number; misses: number }) {
  const total = aces + inPlay + misses;
  if (!total) return null;
  const parts = [
    { key: "ace", label: t("Ace"), value: aces },
    { key: "play", label: t("In play"), value: inPlay },
    { key: "miss", label: t("Miss"), value: misses }
  ].map((part) => ({ ...part, share: (part.value / total) * 100 }));
  return (
    <div className="outcome-mix" aria-label={t("Outcome mix")}>
      <div className="outcome-mix-bar">
        {parts.filter((part) => part.value > 0).map((part) => <i key={part.key} className={part.key} style={{ width: `${part.share}%` }} />)}
      </div>
      <div className="outcome-mix-legend">
        {parts.map((part) => (
          <span key={part.key}><i className={part.key} />{part.label}<strong>{part.share.toFixed(1)}%</strong><small>{part.value.toLocaleString()}</small></span>
        ))}
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, meta, delta }: { icon: ReactNode; label: string; value: string; meta?: string; delta?: ReactNode }) {
  return (
    <div className="kpi">
      {icon}
      <span>{label}</span>
      <strong>{value}</strong>
      <div className="kpi-footer">
        {meta && <small>{meta}</small>}
        {delta}
      </div>
    </div>
  );
}

function DeltaBadge({ current, previous, suffix, inverse = false }: { current: number; previous: number; suffix: string; inverse?: boolean }) {
  const delta = current - previous;
  const isPositive = inverse ? delta <= 0 : delta >= 0;
  return <em className={`delta-badge ${isPositive ? "up" : "down"}`}>{delta >= 0 ? "+" : ""}{delta.toFixed(1)}{suffix}</em>;
}

function TrendCard({ label, value, previous, suffix, precision, inverse = false, featured = false }: { label: string; value: number; previous: number; suffix: string; precision: number; inverse?: boolean; featured?: boolean }) {
  const delta = value - previous;
  const isPositive = inverse ? delta <= 0 : delta >= 0;
  return (
    <article className={`trend-card ${featured ? "featured" : ""}`}>
      <span>{label}</span>
      <strong>{value.toFixed(precision)}{suffix}</strong>
      <em className={isPositive ? "up" : "down"}>{delta >= 0 ? "+" : ""}{delta.toFixed(precision)}{suffix}</em>
    </article>
  );
}

function GoalRow({ label, actual, goal, suffix, tone }: { label: string; actual: number; goal: number; suffix: string; tone: string }) {
  const progress = goal ? Math.min(120, Math.max(0, (actual / goal) * 100)) : 0;
  return (
    <div className="goal-row">
      <div>
        <span>{label}</span>
        <strong>{actual.toFixed(1)}{suffix}</strong>
      </div>
      <div className="goal-track">
        <i className={tone} style={{ width: `${Math.min(100, progress)}%` }} />
      </div>
      <em>{goal.toFixed(1)}{suffix}</em>
    </div>
  );
}

function PlayerCard({ player, metric }: { player: PlayerStats; metric: Metric }) {
  const missRate = player.totalServes ? (player.misses / player.totalServes) * 100 : 0;
  return (
    <article className="player-card">
      <div>
        <img src={player.image || personIcon} alt="" />
        <div>
          <h3>{player.name}</h3>
          <p>#{player.number} · {player.position}</p>
        </div>
      </div>
      <div className="player-card-metrics">
        <span className="player-metric" tabIndex={0}>
          <CircleDot size={14} />{player.totalServes}
          <span className="player-metric-tip" role="tooltip">{t("Serves")}</span>
        </span>
        <span className="player-metric" tabIndex={0}>
          <Trophy size={14} />{player.aces}
          <span className="player-metric-tip" role="tooltip">{t("Aces")}</span>
        </span>
        <strong className="player-metric" tabIndex={0}>
          {player.efficiency.toFixed(1)}%
          <span className="player-metric-tip" role="tooltip">{t("Efficiency")}</span>
        </strong>
      </div>
      <small>{t("{avg} avg · {max} max", { avg: formatSpeed(player.avgSpeed, metric), max: formatSpeed(player.maxSpeed, metric) })}</small>
      <div className="mini-bars" aria-hidden="true">
        <i style={{ height: `${Math.max(6, Math.min(100, player.efficiency))}%` }} />
        <i style={{ height: `${Math.max(6, Math.min(100, player.aces * 4))}%` }} />
        <i style={{ height: `${Math.max(6, Math.min(100, missRate))}%` }} />
      </div>
    </article>
  );
}

function GamesView({ games, metric, opponents, players, serves }: { games: Game[]; metric: Metric; opponents: Opponent[]; players: Player[]; serves: Serve[] }) {
  const [season, setSeason] = useState("all");
  const [opponentId, setOpponentId] = useState("all");
  const [gameId, setGameId] = useState("all");

  const seasons = useMemo(() => [...new Set(games.map(seasonOf))].sort().reverse(), [games]);
  const seasonGames = games.filter((game) => season === "all" || seasonOf(game) === season);
  const opponentGames = seasonGames.filter((game) => opponentId === "all" || game.opponentId === opponentId);
  const scopedGames = opponentGames.filter((game) => gameId === "all" || game.id === gameId);
  const scopedIds = new Set(scopedGames.map((game) => game.id));
  const gameServes = serves.filter((serve) => serve.gameId && scopedIds.has(serve.gameId));

  const scale = displayScale(gameServes);
  const totals = summarize(gameServes);
  const receivers = receiverStats(gameServes);
  const perGame = gameStats(gameServes, scopedGames, opponents);
  const servers = serverStats(gameServes, players);
  // Practice from the whole history against these games
  const pressure = practiceVsGame(serves.filter((serve) => !serve.gameId || scopedIds.has(serve.gameId)), players)
    .map((row) => ({ name: row.name, practice: speedForMetric(row.practice.avgSpeed, metric), game: speedForMetric(row.game.avgSpeed, metric) }));

  const grade = (normalized: number | null) => normalized == null ? "—" : onScale(normalized, scale).toFixed(1);
  const opponentName = (id: string) => opponents.find((o) => o.id === id)?.name ?? t("Opponent");
  const scopeTitle = gameId !== "all" && scopedGames[0]
    ? t("vs {name} · {date}", { name: opponentName(scopedGames[0].opponentId), date: formatGameDate(scopedGames[0].playedOn) })
    : opponentId !== "all" ? t("vs {name}", { name: opponentName(opponentId) }) : t("All games");
  // Serve at: weakest passer with enough graded receptions to trust
  const target = receivers.find((r) => r.graded >= 3 && r.avgGrade != null);
  const receiverChart = receivers.filter((r) => r.avgGrade != null).map((r) => ({
    name: `#${r.number}`,
    pass: Number(onScale(r.avgGrade!, scale).toFixed(2)),
    serves: r.serves
  }));
  const gameChart = [...perGame].reverse().map((row) => ({
    name: `${formatGameDate(row.game.playedOn)} · ${row.opponent}`,
    aceRate: row.aceRate,
    missRate: row.missRate,
    pass: row.avgGrade == null ? null : Number(onScale(row.avgGrade, scale).toFixed(2))
  }));

  if (!games.length) {
    return (
      <div className="stack">
        <EmptyState
          title={t("No games yet")}
          body={t("Create your opponents and their games in the ServeBoost app (Analytics tab), then save serves to a game with the receiver's jersey number and pass grade. Breakdowns by team, game and season appear here.")}
        />
      </div>
    );
  }

  return (
    <div className="dashboard-grid">
      <section className="controls-band">
        <div className="control-label"><SlidersHorizontal size={16} /> {t("Scope")}</div>
        <div className="field">
          <CalendarDays size={16} />
          <select aria-label={t("Season")} value={season} onChange={(event) => { setSeason(event.target.value); setGameId("all"); }}>
            <option value="all">{t("All seasons")}</option>
            {seasons.map((s) => <option key={s} value={s}>{t("Season {season}", { season: s })}</option>)}
          </select>
        </div>
        <div className="field">
          <Swords size={16} />
          <select aria-label={t("Opponent")} value={opponentId} onChange={(event) => { setOpponentId(event.target.value); setGameId("all"); }}>
            <option value="all">{t("All opponents")}</option>
            {opponents.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div className="field">
          <Target size={16} />
          <select aria-label={t("Game")} value={gameId} onChange={(event) => setGameId(event.target.value)}>
            <option value="all">{t("All games")}</option>
            {opponentGames.map((game) => <option key={game.id} value={game.id}>{formatGameDate(game.playedOn)} · {opponentName(game.opponentId)}</option>)}
          </select>
        </div>
        {(season !== "all" || opponentId !== "all" || gameId !== "all") && (
          <button className="text-button" onClick={() => { setSeason("all"); setOpponentId("all"); setGameId("all"); }}><X size={14} /> {t("Clear")}</button>
        )}
      </section>

      <section className="hero-panel">
        <div>
          <p className="eyebrow">{t("Game serving")}</p>
          <h2>{scopeTitle}</h2>
          <p>
            <strong>{t(scopedGames.length === 1 ? "{count} game serves across {games} game" : "{count} game serves across {games} games", { count: totals.serves.toLocaleString(), games: scopedGames.length.toLocaleString() })}</strong>
            {season !== "all" ? ` ${t("in season {season}", { season })}` : ""}. {t("Their reception averaged {grade} on a {min}–{max} scale", { grade: grade(totals.avgGrade), min: scale[0], max: scale[1] })}
            {totals.perfectRate != null ? t(", with {rate}% perfect passes", { rate: totals.perfectRate.toFixed(0) }) : ""}.
          </p>
          {target && (
            <p className="target-callout"><Target size={14} /><span>{t("Serve at #{number}: {grade} average pass over {count} graded receptions.", { number: target.number, grade: grade(target.avgGrade), count: target.graded })}</span></p>
          )}
          <OutcomeMix aces={totals.aces} inPlay={totals.serves - totals.aces - totals.misses} misses={totals.misses} />
        </div>
        <div className="hero-metrics">
          <Kpi icon={<Trophy />} label={t("Ace rate")} value={`${totals.aceRate.toFixed(1)}%`} meta={t("{count} aces", { count: totals.aces })} />
          <Kpi icon={<AlertTriangle />} label={t("Miss rate")} value={`${totals.missRate.toFixed(1)}%`} meta={t("{count} misses", { count: totals.misses })} />
          <Kpi icon={<Gauge />} label={t("Their pass average")} value={`${grade(totals.avgGrade)} / ${scale[1]}`} meta={t("Average speed {speed}", { speed: formatSpeed(totals.avgSpeed, metric) })} />
        </div>
      </section>

      <section className="section-band">
        <div className="section-heading row-heading">
          <div>
            <p className="eyebrow">{t("Opposing receivers")}</p>
            <h2>{t("Reception by jersey number")}</h2>
          </div>
          <span className="section-chip">{t("Weakest passers first · scale {min}–{max}", { min: scale[0], max: scale[1] })}</span>
        </div>
        {receivers.length ? (
          <div className="table-wrap">
            <table className="compact-table">
              <thead>
                <tr><th>{t("Receiver")}</th><th>{t("Serves received")}</th><th>{t("Aces conceded")}</th><th>{t("Pass average")}</th><th>{t("Perfect passes")}</th><th>{t("Speed faced")}</th></tr>
              </thead>
              <tbody>
                {receivers.map((r) => (
                  <tr key={r.number}>
                    <td><strong>#{r.number}</strong>{target?.number === r.number && <span className="target-pill">{t("Serve here")}</span>}</td>
                    <td>{r.serves}</td>
                    <td>{r.aces} <small className="muted">({r.aceRate.toFixed(0)}%)</small></td>
                    <td>
                      <div className="grade-cell">
                        <span>{grade(r.avgGrade)}</span>
                        <i><b style={{ width: `${Math.round((r.avgGrade ?? 0) * 100)}%` }} /></i>
                      </div>
                    </td>
                    <td>{r.perfectRate == null ? "—" : `${r.perfectRate.toFixed(0)}%`}</td>
                    <td>{formatSpeed(r.avgSpeed, metric)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <NoData />}
      </section>

      <section className="chart-grid">
        <ChartPanel title={t("Pass quality by receiver")} meta={t("Average on {min}–{max} · lower is better for us", { min: scale[0], max: scale[1] })}>
          {receiverChart.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={receiverChart}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis domain={[scale[0], scale[1]]} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,.035)" }} content={<AnalyticsTooltip />} />
                <Bar name={t("Pass average")} dataKey="pass" radius={[5, 5, 0, 0]}>
                  {receiverChart.map((entry) => <Cell key={entry.name} fill={entry.name === `#${target?.number}` ? "#b21818" : "#9ab8ff"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <NoData />}
        </ChartPanel>

        <ChartPanel title={t("Game by game")} meta={t("Ace and miss rate · their pass average")}>
          {gameChart.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={gameChart}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={false} />
                <YAxis yAxisId="rate" domain={[0, "auto"]} tickFormatter={(value) => `${Number(value).toFixed(0)}%`} />
                <YAxis yAxisId="pass" orientation="right" domain={[scale[0], scale[1]]} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,.035)" }} content={<AnalyticsTooltip />} />
                <Legend iconType="circle" iconSize={8} />
                <Bar name={t("Ace rate")} yAxisId="rate" dataKey="aceRate" fill="#6ce9b6" radius={[5, 5, 0, 0]} />
                <Bar name={t("Miss rate")} yAxisId="rate" dataKey="missRate" fill="#ff7568" radius={[5, 5, 0, 0]} />
                <Line name={t("Their pass average")} yAxisId="pass" type="monotone" dataKey="pass" stroke="#e5b54a" strokeWidth={3} dot={{ r: 4, fill: "#0f1412", strokeWidth: 2 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          ) : <NoData />}
        </ChartPanel>
      </section>

      <section className="section-band">
        <div className="section-heading row-heading">
          <div>
            <p className="eyebrow">{t("Games")}</p>
            <h2>{t("Results by game")}</h2>
          </div>
          <span className="section-chip">{t("Select a row to focus on it")}</span>
        </div>
        {perGame.length ? (
          <div className="table-wrap">
            <table className="compact-table">
              <thead>
                <tr><th>{t("Date")}</th><th>{t("Opponent")}</th><th>{t("Season")}</th><th>{t("Serves")}</th><th>{t("Average speed")}</th><th>{t("Ace rate")}</th><th>{t("Miss rate")}</th><th>{t("Their pass average")}</th></tr>
              </thead>
              <tbody>
                {perGame.map((row) => (
                  <tr
                    key={row.game.id}
                    className={`clickable-row ${gameId === row.game.id ? "selected" : ""}`}
                    onClick={() => {
                      if (gameId === row.game.id) {
                        setSeason("all");
                        setOpponentId("all");
                        setGameId("all");
                        return;
                      }
                      setSeason(seasonOf(row.game));
                      setOpponentId(row.game.opponentId);
                      setGameId(row.game.id);
                    }}
                  >
                    <td>{formatGameDate(row.game.playedOn)}</td>
                    <td>{row.opponent}</td>
                    <td>{seasonOf(row.game)}</td>
                    <td>{row.serves}</td>
                    <td>{formatSpeed(row.avgSpeed, metric)}</td>
                    <td>{row.aceRate.toFixed(1)}%</td>
                    <td>{row.missRate.toFixed(1)}%</td>
                    <td>{grade(row.avgGrade)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <NoData />}
      </section>

      <section className="section-band">
        <div className="section-heading row-heading">
          <div>
            <p className="eyebrow">{t("Our servers")}</p>
            <h2>{t("Serving in games")}</h2>
          </div>
          <span className="section-chip">{t("Lower pass average = tougher serve")}</span>
        </div>
        {servers.length ? (
          <div className="table-wrap">
            <table className="compact-table">
              <thead>
                <tr><th>{t("Player")}</th><th>{t("Serves")}</th><th>{t("Average speed")}</th><th>{t("Ace rate")}</th><th>{t("Miss rate")}</th><th>{t("Their pass average")}</th></tr>
              </thead>
              <tbody>
                {servers.map((row) => (
                  <tr key={row.playerId}>
                    <td>{row.number != null ? `#${row.number} ` : ""}{row.name}</td>
                    <td>{row.serves}</td>
                    <td>{formatSpeed(row.avgSpeed, metric)}</td>
                    <td>{row.aceRate.toFixed(1)}%</td>
                    <td>{row.missRate.toFixed(1)}%</td>
                    <td>{grade(row.avgGrade)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <NoData />}
      </section>

      <section className="chart-grid">
        <ChartPanel title={t("Does pace hold up in games?")} meta={t("Average speed · practice vs games · {metric}", { metric })} wide>
          {pressure.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={pressure}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis domain={["dataMin - 5", "dataMax + 5"]} tickFormatter={(value) => Number(value).toFixed(0)} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,.035)" }} content={<AnalyticsTooltip />} />
                <Legend iconType="circle" iconSize={8} />
                <Bar name={t("Practice")} dataKey="practice" fill="rgba(154,184,255,.55)" radius={[5, 5, 0, 0]} />
                <Bar name={t("Games")} dataKey="game" fill="#b21818" radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <NoData />}
        </ChartPanel>
      </section>
    </div>
  );
}

function formatGameDate(isoDate: string) {
  return new Date(`${isoDate}T12:00:00`).toLocaleDateString(dateLocaleTag(), { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function PlayersView({ isDemo, players, uid }: { isDemo: boolean; players: Player[]; uid: string }) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<keyof Player>("firstName");
  const [ascending, setAscending] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);

  const sortedPlayers = useMemo(() => [...players].sort((a, b) => {
    const left = String(a[sortKey] ?? "");
    const right = String(b[sortKey] ?? "");
    return ascending ? left.localeCompare(right, undefined, { numeric: true }) : right.localeCompare(left, undefined, { numeric: true });
  }), [players, sortKey, ascending]);

  function openNewPlayer() {
    setEditingPlayer(null);
    setEditorOpen(true);
  }

  function openEditPlayer(player: Player) {
    setEditingPlayer(player);
    setEditorOpen(true);
  }

  async function deleteSelected() {
    if (isDemo) return;
    const { error } = await supabase.from("players").delete().in("id", [...selected]).eq("user_id", uid);
    if (error) throw error;
    setSelected(new Set());
  }

  const columns: Array<{ key: keyof Player; label: string }> = [
    { key: "firstName", label: t("First name") },
    { key: "lastName", label: t("Last name") },
    { key: "number", label: t("Number") },
    { key: "position", label: t("Position") },
    { key: "dateOfBirth", label: t("Birthday") },
    { key: "height", label: t("Height") },
    { key: "weight", label: t("Weight") },
    { key: "yearsOfEligibility", label: t("Eligibility") }
  ];

  return (
    <div className="stack">
      <section className="section-band">
        <div className="section-heading row-heading">
          <div>
            <p className="eyebrow">{t("Team management")}</p>
            <h2>{t("Roster")}</h2>
          </div>
          <div className="row-actions">
            {isDemo && <span className="section-chip">{t("Read-only demo")}</span>}
            <button className="primary-button" onClick={openNewPlayer} disabled={isDemo}><Plus size={16} /> {t("Add player")}</button>
            <button className="danger-button" onClick={deleteSelected} disabled={!selected.size || isDemo}><Trash2 size={16} /> {t("Delete")}</button>
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th aria-label={t("Select players")} />
                <th aria-label={t("Edit player")} />
                {columns.map((column) => (
                  <th key={column.key}>
                    <button onClick={() => {
                      setSortKey(column.key);
                      setAscending(sortKey === column.key ? !ascending : true);
                    }}>{column.label}</button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedPlayers.map((player) => (
                <tr key={player.id}>
                  <td><input type="checkbox" checked={selected.has(player.id)} onChange={(event) => {
                    const next = new Set(selected);
                    event.target.checked ? next.add(player.id) : next.delete(player.id);
                    setSelected(next);
                  }} /></td>
                  <td><button className="icon-button table-action" onClick={() => openEditPlayer(player)} aria-label={`Edit ${player.firstName} ${player.lastName}`}><Pencil size={15} /></button></td>
                  <td>{player.firstName}</td>
                  <td>{player.lastName}</td>
                  <td>{player.number}</td>
                  <td>{player.position}</td>
                  <td>{player.dateOfBirth || "—"}</td>
                  <td>{formatMeasure(player.height)}</td>
                  <td>{formatMeasure(player.weight)}</td>
                  <td>{player.yearsOfEligibility || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="roster-summary-grid">
        <RosterSummary label={t("Players")} value={players.length.toLocaleString()} />
        <RosterSummary label={t("Positions")} value={new Set(players.map((player) => player.position).filter(Boolean)).size.toLocaleString()} />
        <RosterSummary label={t("Editable fields")} value="8" />
      </section>

      {editorOpen && (
        <PlayerEditor
          initialPlayer={editingPlayer}
          isDemo={isDemo}
          uid={uid}
          onClose={() => setEditorOpen(false)}
        />
      )}
    </div>
  );
}

function RosterSummary({ label, value }: { label: string; value: string }) {
  return (
    <article className="roster-summary">
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function PlayerEditor({ initialPlayer, isDemo, uid, onClose }: { initialPlayer: Player | null; isDemo: boolean; uid: string; onClose: () => void }) {
  const [draft, setDraft] = useState<PlayerDraft>(() => initialPlayer ? playerToDraft(initialPlayer) : emptyPlayerDraft());
  const [photo, setPhoto] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isEdit = Boolean(initialPlayer);

  async function savePlayer(event: FormEvent) {
    event.preventDefault();
    setError("");

    if (isDemo) {
      setError(t("Demo roster is read-only. Sign in with a Supabase account to save changes."));
      return;
    }

    if (!draft.firstName.trim() || !draft.lastName.trim()) {
      setError(t("First and last name are required."));
      return;
    }

    if (draft.number && Number.isNaN(Number(draft.number))) {
      setError(t("Number must be numeric."));
      return;
    }

    setBusy(true);
    try {
      let image = draft.image;
      if (photo) {
        const extension = photo.name.split(".").pop() || "jpg";
        const path = `${uid}/${initialPlayer?.id ?? crypto.randomUUID()}.${extension}`;
        const { error: uploadError } = await supabase.storage.from("player-avatars").upload(path, photo, { upsert: true });
        if (uploadError) throw uploadError;
        image = supabase.storage.from("player-avatars").getPublicUrl(path).data.publicUrl;
      }

      const payload = {
        user_id: uid,
        first_name: draft.firstName.trim(),
        last_name: draft.lastName.trim(),
        number: Number(draft.number || 0),
        position: draft.position,
        birth_date: draft.dateOfBirth || null,
        height_cm: draft.height && Number.isFinite(Number(draft.height)) ? Math.round(Number(draft.height)) : null,
        weight_kg: draft.weight && Number.isFinite(Number(draft.weight)) ? Math.round(Number(draft.weight)) : null,
        eligibility_years: draft.yearsOfEligibility ? Number(draft.yearsOfEligibility) : null,
        avatar_url: image || null
      };

      if (initialPlayer) {
        const { error: updateError } = await supabase.from("players").update(payload).eq("id", initialPlayer.id).eq("user_id", uid);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase.from("players").insert(payload);
        if (insertError) throw insertError;
      }

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Could not save player."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="editor-shell" role="dialog" aria-modal="true" aria-label={isEdit ? t("Edit player") : t("Add player")}>
      <button className="editor-scrim" onClick={onClose} aria-label={t("Close player editor")} />
      <aside className="player-editor">
        <div className="editor-heading">
          <div>
            <p className="eyebrow">{t("Roster editor")}</p>
            <h2>{isEdit ? t("Edit player") : t("Add player")}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label={t("Close editor")}><X size={18} /></button>
        </div>

        <form className="editor-form" onSubmit={savePlayer}>
          <div className="photo-field">
            <img src={draft.image || personIcon} alt="" />
            <label>
              <Upload size={16} />
              <span>{photo ? photo.name : t("Upload photo")}</span>
              <input type="file" accept="image/*" onChange={(event) => setPhoto(event.target.files?.[0] ?? null)} />
            </label>
          </div>
          <label>{t("First name")}<input value={draft.firstName} onChange={(event) => setDraft({ ...draft, firstName: event.target.value })} /></label>
          <label>{t("Last name")}<input value={draft.lastName} onChange={(event) => setDraft({ ...draft, lastName: event.target.value })} /></label>
          <label>{t("Number")}<input type="number" value={draft.number} onChange={(event) => setDraft({ ...draft, number: event.target.value })} /></label>
          <label>{t("Position")}<select value={draft.position} onChange={(event) => setDraft({ ...draft, position: event.target.value })}>
            <option>{t("Outside Hitter")}</option>
            <option>{t("Opposite")}</option>
            <option>{t("Setter")}</option>
            <option>{t("Middle Blocker")}</option>
            <option>{t("Libero")}</option>
          </select></label>
          <label>{t("Date of birth")}<input type="date" value={draft.dateOfBirth} onChange={(event) => setDraft({ ...draft, dateOfBirth: event.target.value })} /></label>
          <label>{t("Height")}<input value={draft.height} onChange={(event) => setDraft({ ...draft, height: event.target.value })} /></label>
          <label>{t("Weight")}<input value={draft.weight} onChange={(event) => setDraft({ ...draft, weight: event.target.value })} /></label>
          <label>{t("Eligibility")}<input value={draft.yearsOfEligibility} onChange={(event) => setDraft({ ...draft, yearsOfEligibility: event.target.value })} /></label>
          <label className="wide">{t("Image URL")}<input value={draft.image} onChange={(event) => setDraft({ ...draft, image: event.target.value })} /></label>
          {error && <p className="form-error">{error}</p>}
          <div className="editor-actions">
            <button type="button" className="ghost-button" onClick={onClose}>{t("Cancel")}</button>
            <button className="primary-button" disabled={busy}>{busy ? t("Saving...") : t("Save player")}</button>
          </div>
        </form>
      </aside>
    </div>
  );
}

function LanguageSelect() {
  const { locale, setLocale } = useLocale();
  return (
    <select aria-label={t("Language")} value={locale} onChange={(event) => setLocale(event.target.value as "en" | "fr")}>
      <option value="en">{t("English")}</option>
      <option value="fr">{t("Français")}</option>
    </select>
  );
}

function SettingsView({ goals, goalDocId, metric, onMetricChange, user }: { goals: Goals; goalDocId: string; metric: Metric; onMetricChange: (metric: Metric) => void; user: AppUser }) {
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<"success" | "error">("success");
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  function requestPasswordChange(event: FormEvent) {
    event.preventDefault();
    setMessage("");
    if (user.isDemo) {
      setMessageTone("error");
      setMessage(t("Demo accounts cannot change passwords."));
      return;
    }
    setConfirming(true);
  }

  async function confirmPasswordChange() {
    if (user.isDemo) {
      setMessageTone("error");
      setMessage(t("Demo accounts cannot change passwords."));
      setConfirming(false);
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setPassword("");
      setConfirming(false);
      setMessageTone("success");
      setMessage(t("Password updated. Use this password the next time you sign in."));
    } catch (err) {
      setMessageTone("error");
      setMessage(err instanceof Error ? err.message : t("Could not update password."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-grid">
      <section className="setting-row">
        <UserRound />
        <div>
          <h2>{t("Account")}</h2>
          <p>{user.email ?? t("Signed in user")}</p>
        </div>
        <button className="ghost-button" onClick={() => user.isDemo ? localStorage.removeItem("serveboost-demo") : void supabase.auth.signOut()}><LogOut size={16} /> {t("Sign out")}</button>
      </section>
      <section className="setting-row">
        <Languages />
        <div>
          <h2>{t("Language")}</h2>
          <p>{t("Workspace display language")}</p>
        </div>
        <LanguageSelect />
      </section>
      <section className="setting-row">
        <Gauge />
        <div>
          <h2>{t("Speed metric")}</h2>
          <p>{t("Used across charts and summaries")}</p>
        </div>
        <select value={metric} onChange={(event) => onMetricChange(event.target.value as Metric)}>
          <option>km/h</option>
          <option>mph</option>
        </select>
      </section>
      <section className="setting-row password-row">
        <KeyRound />
        <div>
          <h2>{t("Password")}</h2>
          <p>{t("This replaces the password you use to sign in.")}</p>
          {confirming ? (
            <div className="password-confirm" role="alertdialog" aria-label={t("Confirm password change")}>
              <p>{t("Update your password now? You will need the new password the next time you sign in.")}</p>
              <div>
                <button type="button" className="ghost-button" onClick={() => setConfirming(false)} disabled={saving}>{t("Cancel")}</button>
                <button type="button" className="primary-button" onClick={() => void confirmPasswordChange()} disabled={saving}>
                  <ShieldCheck size={16} /> {saving ? t("Updating…") : t("Yes, update password")}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={requestPasswordChange}>
              <input type="password" minLength={8} required value={password} onChange={(event) => setPassword(event.target.value)} placeholder={t("New password")} />
              <button className="primary-button"><ShieldCheck size={16} /> {t("Save")}</button>
            </form>
          )}
          {message && <p className={messageTone === "error" ? "form-error" : "success-text"}>{message}</p>}
        </div>
      </section>
      <GoalSettings goals={goals} goalDocId={goalDocId} user={user} />
    </div>
  );
}

function GoalSettings({ goals, goalDocId, user }: { goals: Goals; goalDocId: string; user: AppUser }) {
  const [draft, setDraft] = useState(goals);
  const [status, setStatus] = useState("");

  useEffect(() => setDraft(goals), [goals]);

  async function saveGoals(event: FormEvent) {
    event.preventDefault();
    setStatus("");
    if (user.isDemo) {
      setStatus(t("Demo goals are read-only. Sign in to save custom targets."));
      return;
    }
    const { error } = await supabase.from("goals").upsert({
      user_id: goalDocId || user.uid,
      avg_speed_float: draft.avgSpeedFloat,
      avg_speed_hybrid: draft.avgSpeedHybrid,
      avg_speed_spin: draft.avgSpeedSpin,
      efficiency_float: draft.effiencyFloat,
      efficiency_hybrid: draft.effiencyHybrid,
      efficiency_spin: draft.effiencySpin,
      updated_at: new Date().toISOString()
    });
    if (error) throw error;
    setStatus(t("Goals updated."));
  }

  const fields: Array<{ key: keyof Goals; label: string; suffix: string }> = [
    { key: "effiencyFloat", label: t("Float efficiency"), suffix: "%" },
    { key: "effiencyHybrid", label: t("Hybrid efficiency"), suffix: "%" },
    { key: "effiencySpin", label: t("Spin efficiency"), suffix: "%" },
    { key: "avgSpeedFloat", label: t("Float avg speed"), suffix: "km/h" },
    { key: "avgSpeedHybrid", label: t("Hybrid avg speed"), suffix: "km/h" },
    { key: "avgSpeedSpin", label: t("Spin avg speed"), suffix: "km/h" }
  ];

  return (
    <section className="goals-settings">
      <div className="section-heading row-heading">
        <div>
          <p className="eyebrow">{t("Coach targets")}</p>
          <h2>{t("Goals")}</h2>
        </div>
        {user.isDemo && <span className="section-chip">{t("Read-only demo")}</span>}
      </div>
      <form className="goals-form" onSubmit={saveGoals}>
        {fields.map((field) => (
          <label key={field.key}>
            <span>{field.label}</span>
            <div>
              <input
                type="number"
                value={draft[field.key]}
                onChange={(event) => setDraft({ ...draft, [field.key]: Number(event.target.value) })}
              />
              <em>{field.suffix}</em>
            </div>
          </label>
        ))}
        <button className="primary-button">{t("Save goals")}</button>
      </form>
      {status && <p className={user.isDemo ? "form-error" : "success-text"}>{status}</p>}
    </section>
  );
}

function Login({ onDemo }: { onDemo: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [socialProvider, setSocialProvider] = useState<"apple" | "google" | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    try {
      if (!isSupabaseConfigured) throw new Error(t("Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY before signing in."));
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) throw signInError;
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Could not sign in."));
    }
  }

  async function resetPassword() {
    if (!email) {
      setError(t("Enter your email first."));
      return;
    }
    if (!isSupabaseConfigured) {
      setError(t("Add the Supabase environment variables before resetting a password."));
      return;
    }
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
    if (resetError) {
      setError(resetError.message);
      return;
    }
    setResetSent(true);
  }

  async function signInWithProvider(provider: "apple" | "google") {
    setError("");
    setSocialProvider(provider);
    try {
      if (!isSupabaseConfigured) throw new Error(t("Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY before signing in."));
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: `${window.location.origin}/dashboard` }
      });
      if (oauthError) throw oauthError;
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Could not start social sign-in."));
      setSocialProvider(null);
    }
  }

  return (
    <main className="login-screen">
      <section className="login-form-panel">
        <div className="login-brand">
          <img src={serveBoostLogo} alt={t("ServeBoost")} />
          <p>{t("Team serve analytics for coaches who need the signal quickly.")}</p>
        </div>
        <form className="login-form" onSubmit={submit}>
          <h2>{t("Sign in")}</h2>
          <div className="social-login" aria-label={t("Sign in with a provider")}>
            <button type="button" className="social-button" disabled={socialProvider !== null} onClick={() => void signInWithProvider("apple")}>
              <span className="social-mark apple-mark"></span>
              {socialProvider === "apple" ? t("Opening Apple…") : t("Continue with Apple")}
            </button>
            <button type="button" className="social-button" disabled={socialProvider !== null} onClick={() => void signInWithProvider("google")}>
              <span className="social-mark google-mark">G</span>
              {socialProvider === "google" ? t("Opening Google…") : t("Continue with Google")}
            </button>
          </div>
          <div className="login-divider"><span>{t("or continue with email")}</span></div>
          <label><Mail size={16} /> <input type="email" placeholder={t("Email")} value={email} onChange={(event) => setEmail(event.target.value)} /></label>
          <label><Lock size={16} /> <input type="password" placeholder={t("Password")} value={password} onChange={(event) => setPassword(event.target.value)} /></label>
          {error && <p className="form-error">{error}</p>}
          {resetSent && <p className="success-text">{t("Reset email sent.")}</p>}
          <button className="primary-button">{t("Sign in")}</button>
          <button type="button" className="ghost-button" onClick={onDemo}><Activity size={16} /> {t("Try demo data")}</button>
          <button type="button" className="text-button" onClick={resetPassword}>{t("Forgot password?")}</button>
        </form>
      </section>
    </main>
  );
}

function DataIssueBanner({ issue }: { issue: DataIssue }) {
  const icon = issue.code === "permission-denied" ? <ShieldCheck /> : issue.code === "unavailable" ? <RefreshCw /> : <AlertTriangle />;
  return (
    <section className={`data-issue ${issue.code}`}>
      {icon}
      <div>
        <h2>{issue.title}</h2>
        <p>{issue.message}</p>
      </div>
    </section>
  );
}

function verticalAxisTitle(title: string) {
  return {
    value: title,
    content: ({ viewBox }: { viewBox?: { x?: number; y?: number; height?: number } }) => {
      if (viewBox?.x == null || viewBox.y == null || viewBox.height == null) return null;
      const x = viewBox.x + 11;
      const y = viewBox.y + viewBox.height / 2;
      return (
        <text x={x} y={y} textAnchor="middle" dominantBaseline="central" transform={`rotate(-90 ${x} ${y})`} className="recharts-text axis-title">
          {title}
        </text>
      );
    }
  };
}

function ChartPanel({ title, meta, children, wide = false }: { title: string; meta?: string; children: ReactNode; wide?: boolean }) {
  return (
    <section className={`chart-panel ${wide ? "chart-panel-wide" : ""}`}>
      <div className="chart-heading">
        <h2>{title}</h2>
        {meta && <span>{meta}</span>}
      </div>
      {children}
    </section>
  );
}

type ChartTooltipEntry = {
  color?: string;
  dataKey?: string;
  name?: string;
  value?: number | string;
  payload?: Record<string, string | number>;
};

function AnalyticsTooltip({ active, payload, label }: { active?: boolean; payload?: ChartTooltipEntry[]; label?: string }) {
  if (!active || !payload?.length) return null;
  const formatValue = (entry: ChartTooltipEntry) => {
    if (typeof entry.value !== "number") return entry.value;
    const key = String(entry.dataKey ?? entry.name ?? "").toLowerCase();
    if (key.includes("rate") || key.includes("efficiency") || key.includes("probability")) return `${entry.value.toFixed(1)}%`;
    return entry.value.toLocaleString(undefined, { maximumFractionDigits: 1 });
  };
  return (
    <div className="chart-tooltip">
      {label && <strong>{label}</strong>}
      {payload.map((entry) => <span key={`${entry.name}-${entry.dataKey}`}><i style={{ background: entry.color }} />{entry.name}: {formatValue(entry)}</span>)}
    </div>
  );
}

function PlayerMapTooltip({ active, payload, metric }: { active?: boolean; payload?: ChartTooltipEntry[]; metric: Metric }) {
  const player = payload?.[0]?.payload;
  if (!active || !player) return null;
  return (
    <div className="chart-tooltip player-map-tooltip">
      <strong>{player.name}</strong>
      <span>{t("Average speed:")} {Number(player.speed).toFixed(1)} {metric}</span>
      <span>{t("Ace rate:")} {Number(player.aceRate).toFixed(1)}%</span>
      <span>{t("Miss rate:")} {Number(player.missRate).toFixed(1)}%</span>
      <span>{t("Serve volume:")} {Number(player.volume).toLocaleString()}</span>
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <section className="empty-state">
      <UserRound />
      <h2>{title}</h2>
      <p>{body}</p>
    </section>
  );
}

function LoadingPanel() {
  return <div className="loading-panel"><Activity className="spin" /> {t("Loading dashboard")}</div>;
}

function NoData() {
  return <div className="no-data">{t("No data available")}</div>;
}
